/**
 * Facility rounds: the provider marks residents SEEN as they move through the building, and the manifest shows the
 * progress (Isaiah Cotto, 2026-10-27: "we should be marking the leads seen as they complete their rounds so they know
 * who they've visited and their dispositions"). docs/provider/FACILITY_ROUNDS.md.
 *
 * Placement follows Isaiah's annotated screenshot: the seen control sits on each row's chip line, beside the testing
 * chip, with today's outcome; the progress header and the Remaining / Seen / All control sit with the Worklist and
 * Walk order controls; the legend gains the seen marks, drawn as they look on the rows.
 *
 * Everything here is held by one hook (useRounds) that FieldManifest calls once, and shared through RoundsContext so
 * the resident workbench (a portal) reads the same state. Seen is the SERVER's: stored per building, visit day and
 * resident with who and when, so a refresh, another device or a second provider covering the building sees it. A row
 * the viewer cannot act on (chart locked) gets no seen control and is not counted. Before the migration, or on an
 * older server, the round reads as "off" and nothing here renders.
 *
 * 2026-10-27, later (Renata Silva via April Torino, approved by Isaiah Cotto): FILTER the residents (testing chip,
 * seen, today's outcome, priority, floor) and flag a PRIORITY (star, optional reason <= 100 characters, shared, not per
 * day), with "Priority first" as a walk order. The roster is paged (50 a page); a filter over one page would lie about
 * totals, so while the view is narrowed (or priority-first) on a worklist of more than one page, the whole worklist is
 * read once (up to the server's 500) and filtered here. "Showing x of y" is always the whole worklist's y.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Check, CheckCheck, Circle, History, ListPlus, SlidersHorizontal, Star, Undo2 } from "lucide-react";
import { Button } from "../../../components/shadcn/ui/button";
import { ToggleGroup, ToggleGroupItem } from "../../../components/shadcn/ui/toggle-group";
import type { DaySheet, RosterPatient } from "../providerApi";
import { patientName, providerApi, testingTone } from "../providerApi";
import { FieldSelect, StatusBadge } from "./FieldPrimitives";
import type { BadgeTone } from "./FieldPrimitives";
import { PRIORITY_REASON_MAX, isRound, roundsApi } from "./roundsApi";
import { CloseOutVisit } from "./CloseOutVisit";
import type { Round, RoundAddedBack, RoundHistory, RoundPriority, RoundResident, SeenResult } from "./roundsApi";
import "./facility-rounds.css";

export type RoundsFilter = "remaining" | "seen" | "all";
type Status = "idle" | "loading" | "ready" | "off" | "error";

/** How long a row just marked seen stays in "Remaining" (receded, with its undo) before it steps out of the list. */
export const LINGER_MS = 6000;
/** How long the undo bar stays. */
export const UNDO_MS = 8000;
/** How often an open round asks the server again (another provider's marks), while visible and online. */
export const POLL_MS = 60000;

const OUTCOME_TONE: Record<string, BadgeTone> = {
  TWL_Q: "green",
  TWL_PRIVATE_PAY: "amber",
  TWL_MEDICAID_PENDING: "amber",
  TWL_DNQ: "rose",
  TWL_HOSPICE: "neutral",
};

/** The legend's testing groups (testingTone), as filter keys. */
export const TESTING_FILTERS = [
  { key: "ok", label: "Qualified", tone: "green" },
  { key: "attn", label: "Verify first", tone: "amber" },
  { key: "stop", label: "Do not test", tone: "rose" },
  { key: "none", label: "Not recorded", tone: "neutral" },
] as const;
/** Today's outcome, as the chips call it, and "none recorded". */
export const OUTCOME_FILTERS = [
  { key: "TWL_Q", label: "TWL Q" },
  { key: "TWL_PRIVATE_PAY", label: "TWL · Private pay" },
  { key: "TWL_MEDICAID_PENDING", label: "TWL · Medicaid pending" },
  { key: "TWL_DNQ", label: "TWL · DNQ" },
  { key: "TWL_HOSPICE", label: "TWL · Hospice" },
  { key: "NONE", label: "No outcome yet" },
] as const;
/** The server's largest page (daySheetService MAX_ROSTER_LIMIT): the most a filter reads at once. */
export const WHOLE_LIMIT = 500;

export interface RoundFilters {
  testing: string[];
  outcomes: string[];
  priorityOnly: boolean;
  floor: string;
}
export const NO_FILTERS: RoundFilters = { testing: [], outcomes: [], priorityOnly: false, floor: "" };

/** A room's floor, cheaply: the leading number's hundreds ("214-B" is floor 2); rooms under 100, others, none. */
export function floorOf(room: string | null | undefined): string {
  if (!room || !String(room).trim()) return "none";
  const m = /^\s*(\d+)/.exec(String(room));
  if (!m) return "other";
  const n = parseInt(m[1], 10);
  return n < 100 ? "low" : String(Math.floor(n / 100));
}
export function floorLabel(key: string): string {
  if (key === "none") return "Room not recorded";
  if (key === "other") return "Other rooms";
  if (key === "low") return "Rooms 1–99";
  return `Floor ${key} · ${key}00s`;
}

const time = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";

function reducedMotion(): boolean {
  try {
    return typeof window !== "undefined" && typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export interface RoundsState {
  status: Status;
  round: Round | null;
  filter: RoundsFilter;
  setFilter: (f: RoundsFilter) => void;
  /** This row is one the viewer may act on (care-related), whether or not they may mark here now. */
  entry: (id: string) => RoundResident | undefined;
  /** Whether the row is listed under the current filter (always true when the round is not on). */
  shows: (id: string) => boolean;
  mark: (patient: RosterPatient, seen: boolean) => Promise<void>;
  pending: (id: string) => boolean;
  canMark: boolean;
  offline: boolean;
  next: () => RoundResident | null;
  goNext: () => void;
  focusId: string | null;
  undo: { id: string; name: string; seen: boolean } | null;
  undoLast: () => void;
  message: string;
  /** The workbench saved notes or tags for this resident: offer "seen" there. */
  offered: string | null;
  offer: (id: string) => void;
  rowClass: (id: string) => string;
  /** The facet filters (AND across facets, OR within one); the seen filter is `filter`. */
  filters: RoundFilters;
  setFilters: (f: RoundFilters) => void;
  resetFilters: () => void;
  /** Non-default filters in force (the seen filter counts when it is "Seen"). */
  activeCount: number;
  /** Whether a row passes the seen filter and every facet. */
  matches: (p: RosterPatient) => boolean;
  /** The rows to list: the whole worklist when it was read for a narrowed view, else the page. */
  listRows: (pageRows: RosterPatient[]) => RosterPatient[];
  /** The listed order: priority residents first (in walk order) when the walk order is "Priority first". */
  arrange: (rows: RosterPatient[]) => RosterPatient[];
  priorityFirst: boolean;
  setPriorityFirst: (on: boolean) => void;
  priorityReady: boolean;
  setPriority: (patient: RosterPatient, priority: boolean, reason?: string | null) => Promise<boolean>;
  /** "Add back" (20261027i) is switched on here. */
  addBackReady: boolean;
  setAddBack: (patient: RosterPatient, add: boolean, reason?: string | null) => Promise<boolean>;
  facilityId: string | undefined;
  /** The whole worklist was read (narrowed view of a multi-page worklist); capped when it exceeds WHOLE_LIMIT. */
  whole: { wanted: boolean; loaded: boolean; loading: boolean; capped: boolean; total: number };
}

export const RoundsContext = createContext<RoundsState | null>(null);

/**
 * The round for the sheet on screen. Called once by FieldManifest before its early return (a hook), with the sheet
 * (or null), and the two things it needs from the manifest: paging (for "Next resident" on another page) and a way to
 * clear the search box when the next resident is hidden by it.
 */
export function useRounds({
  sheet,
  offline,
  onPage,
  onRevealNeeded,
}: {
  sheet: DaySheet | null | undefined;
  offline: boolean;
  onPage: (offset: number) => void;
  onRevealNeeded?: (id: string) => boolean;
}): RoundsState {
  const [round, setRound] = useState<Round | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [filter, setFilter] = useState<RoundsFilter>("remaining");
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set());
  const [lingering, setLingering] = useState<Set<string>>(() => new Set());
  const [undo, setUndo] = useState<RoundsState["undo"]>(null);
  const [message, setMessage] = useState("");
  const [focusId, setFocusId] = useState<string | null>(null);
  const [offered, setOffered] = useState<string | null>(null);
  const [filters, setFilters] = useState<RoundFilters>(NO_FILTERS);
  const [priorityFirst, setPriorityFirst] = useState(false);
  const [wholeRows, setWholeRows] = useState<RosterPatient[] | null>(null);
  const [wholeLoading, setWholeLoading] = useState(false);
  const anchor = useRef<string | null>(null);
  const wholeSeq = useRef(0);
  const seq = useRef(0);
  const timers = useRef<number[]>([]);
  const facilityId = sheet?.facility?.id;
  const lens = sheet?.roster?.lens;
  const order = sheet?.roster?.order;

  const load = useCallback(async (quiet: boolean) => {
    if (!facilityId || !lens || !order) return;
    seq.current += 1;
    const mine = seq.current;
    if (!quiet) setStatus((s) => (s === "ready" ? s : "loading"));
    try {
      const r = await roundsApi.get(facilityId, lens, order);
      if (mine !== seq.current) return; // a newer read is on its way
      if (r && r.success && isRound(r.data)) {
        setRound(r.data);
        setStatus("ready");
      } else if (r && (r.status === 503 || r.status === 404 || r.status === 403 || (r.success && !isRound(r.data)))) {
        // Not switched on yet (before 20261027c), not offered, or an older server: no seen controls at all.
        setRound(null);
        setStatus("off");
      } else if (!quiet) {
        setStatus("error");
      }
    } catch {
      if (mine === seq.current && !quiet) setStatus("error");
    }
  }, [facilityId, lens, order]);

  // Every new sheet (a reload after a TWL Q mark, a worklist or walk-order change, paging) reads the round again.
  useEffect(() => {
    if (!sheet) return;
    load(false);
  }, [sheet, load]);

  // Another provider's marks arrive without a reload: ask again every minute while the page is visible and online.
  useEffect(() => {
    if (status !== "ready" || offline) return undefined;
    const t = window.setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      if (pendingIds.size) return;
      load(true);
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [status, offline, load, pendingIds.size]);

  useEffect(() => () => { timers.current.forEach((t) => window.clearTimeout(t)); }, []);
  const later = (fn: () => void, ms: number) => { timers.current.push(window.setTimeout(fn, ms)); };

  // A different building or worklist starts with Remaining and a clean slate.
  useEffect(() => {
    setFilter("remaining");
    setLingering(new Set());
    setUndo(null);
    setOffered(null);
    setFilters(NO_FILTERS);
    setPriorityFirst(false);
    anchor.current = null;
  }, [facilityId, lens]);

  const byId = useMemo(() => new Map((round?.residents || []).map((r) => [r.id, r])), [round]);
  const canMark = Boolean(round && round.can_mark);

  const apply = useCallback((id: string, view: Pick<RoundResident, "seen" | "source" | "by" | "at">) => {
    setRound((cur) => {
      if (!cur) return cur;
      const residents = cur.residents.map((r) => (r.id === id ? { ...r, ...view } : r));
      const seen = residents.filter((r) => r.seen);
      const last = seen.reduce<string | null>((t, r) => (r.at && (!t || r.at > t) ? r.at : t), null);
      return {
        ...cur,
        residents,
        progress: {
          total: residents.length,
          seen: seen.length,
          remaining: residents.length - seen.length,
          last_seen_at: last,
          complete: residents.length > 0 && seen.length === residents.length,
        },
      };
    });
  }, []);

  const mark = useCallback(async (patient: RosterPatient, seen: boolean) => {
    const id = patient.id;
    const before = byId.get(id);
    if (!facilityId || !before || !canMark || offline || pendingIds.has(id)) return;
    const name = patientName(patient);
    setMessage("");
    setPendingIds((s) => new Set(s).add(id));
    // Shown at once; the server's answer replaces it, and a failure puts it back.
    apply(id, seen
      ? { seen: true, source: "MANUAL", by: { first_name: "You", mine: true }, at: new Date().toISOString() }
      : { seen: false, source: null, by: null, at: null });
    if (seen) {
      anchor.current = id;
      setLingering((s) => new Set(s).add(id));
      later(() => setLingering((s) => { const n = new Set(s); n.delete(id); return n; }), LINGER_MS);
    }
    try {
      const r = await roundsApi.mark(facilityId, id, seen);
      if (r && r.success && r.data) {
        const d: SeenResult = r.data;
        apply(id, { seen: d.seen, source: d.source, by: d.by, at: d.at });
        setUndo({ id, name, seen });
        later(() => setUndo((u) => (u && u.id === id && u.seen === seen ? null : u)), UNDO_MS);
        if (seen && !d.changed && d.by && !d.by.mine) setMessage(`${name} was already marked seen by ${d.by.first_name} at ${time(d.at)}.`);
        if (offered === id) setOffered(null);
      } else {
        apply(id, before);
        if (r && r.status === 503) setStatus("off");
        setMessage(r && (r.message || r.error)
          ? `${r.message || r.error}`
          : `eMerge did not confirm that. ${name} is shown as it was; try again.`);
      }
    } catch {
      apply(id, before);
      setMessage(`eMerge did not confirm that. ${name} is shown as it was; try again.`);
    } finally {
      setPendingIds((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  }, [apply, byId, canMark, facilityId, offline, offered, pendingIds]);

  // The walk order on screen: the server's (Room or Name), with priority residents first under "Priority first".
  const walk = useMemo((): RoundResident[] => {
    const list = round?.residents || [];
    return priorityFirst ? [...list.filter((r) => r.priority), ...list.filter((r) => !r.priority)] : list;
  }, [round, priorityFirst]);
  const next = useCallback((): RoundResident | null => {
    const open = walk.filter((r) => !r.seen);
    if (!open.length) return null;
    const at = anchor.current ? walk.findIndex((r) => r.id === anchor.current) : -1;
    return walk.slice(at + 1).find((r) => !r.seen) || open[0];
  }, [walk]);

  const roster = sheet?.roster;
  const goNext = useCallback(() => {
    const target = next();
    if (!target || !roster) return;
    anchor.current = target.id;
    const limit = roster.limit || 100;
    const onThisPage = wholeRows
      ? wholeRows.some((r) => r.id === target.id)
      : target.position >= roster.offset && target.position < roster.offset + roster.returned;
    setFocusId(target.id);
    if (!onThisPage) onPage(Math.floor(target.position / limit) * limit);
    else if (onRevealNeeded) onRevealNeeded(target.id);
  }, [next, roster, onPage, onRevealNeeded, wholeRows]);

  // Bring the next resident into view once their row is on screen (now, or after the page loads).
  useEffect(() => {
    if (!focusId) return undefined;
    let clear: number | undefined;
    const t = window.setTimeout(() => {
      const row = document.querySelector(`[data-rounds-row="${focusId}"]`) as HTMLElement | null;
      if (!row) return; // another page is loading: this runs again with the new sheet
      if (typeof row.scrollIntoView === "function") row.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "center" });
      const control = row.querySelector("[data-seen-control]") as HTMLElement | null;
      (control || row).focus({ preventScroll: true });
      // The highlight fades after a moment; the row stays where it is.
      clear = window.setTimeout(() => setFocusId((f) => (f === focusId ? null : f)), 2400);
    }, 30);
    return () => { window.clearTimeout(t); if (clear) window.clearTimeout(clear); };
  }, [focusId, sheet]);

  const on = status === "ready" && Boolean(round);
  const shows = useCallback((id: string) => {
    if (!on) return true;
    if (filter === "all") return true;
    const e = byId.get(id);
    if (filter === "seen") return Boolean(e && e.seen);
    return !(e && e.seen) || lingering.has(id) || focusId === id;
  }, [on, filter, byId, lingering, focusId]);

  const activeCount = (filters.testing.length ? 1 : 0) + (filters.outcomes.length ? 1 : 0)
    + (filters.priorityOnly ? 1 : 0) + (filters.floor ? 1 : 0) + (on && filter === "seen" ? 1 : 0);
  const matches = useCallback((p: RosterPatient) => {
    if (!shows(p.id)) return false;
    if (!on) return true;
    const e = byId.get(p.id);
    if (filters.testing.length && !filters.testing.includes(testingTone(p.testing_status) || "none")) return false;
    if (filters.floor && floorOf(p.room_number) !== filters.floor) return false;
    // The round's own facts are known only for the viewer's residents: a locked row never passes them.
    if (filters.outcomes.length && !(e && filters.outcomes.includes(e.outcome ? e.outcome.key : "NONE"))) return false;
    if (filters.priorityOnly && !(e && e.priority)) return false;
    return true;
  }, [shows, on, byId, filters]);

  // Narrowed BEYOND THE DEFAULT (Seen, any facet, or Priority first) on a worklist longer than the page: the whole
  // worklist is read once, so the filter and "Showing x of y" tell the truth. The default Remaining (and All) page as
  // before, with no extra read (verifier 2026-10-27: Remaining on every load doubled the day-sheet reads); the
  // header then says the list is this page, and the round's own counts (the rounds read, the whole worklist) stand.
  const narrowedView = on && (activeCount > 0 || priorityFirst);
  const multiPage = Boolean(roster && roster.total > roster.returned);
  const wantWhole = Boolean(narrowedView && multiPage && facilityId && lens && order);
  useEffect(() => {
    if (!wantWhole || !sheet || !facilityId || !lens || !order) { setWholeRows(null); setWholeLoading(false); return; }
    wholeSeq.current += 1;
    const mine = wholeSeq.current;
    setWholeLoading(true);
    providerApi.getDaySheet(facilityId, { limit: Math.min(sheet.roster.total, WHOLE_LIMIT), offset: 0, lens: lens as never, order: order as never })
      .then((r) => {
        if (mine !== wholeSeq.current) return;
        setWholeRows(r && r.success && r.data && r.data.facility && r.data.facility.id === facilityId ? r.data.roster.rows : null);
      })
      .catch(() => { if (mine === wholeSeq.current) setWholeRows(null); })
      .finally(() => { if (mine === wholeSeq.current) setWholeLoading(false); });
  }, [wantWhole, sheet, facilityId, lens, order]);

  const listRows = useCallback((pageRows: RosterPatient[]) => (wholeRows && wantWhole ? wholeRows : pageRows), [wholeRows, wantWhole]);
  const arrange = useCallback((rows: RosterPatient[]) => {
    if (!on || !priorityFirst) return rows;
    const prio = rows.filter((r) => byId.get(r.id)?.priority);
    return [...prio, ...rows.filter((r) => !byId.get(r.id)?.priority)];
  }, [on, priorityFirst, byId]);

  const setPriority = useCallback(async (patient: RosterPatient, priority: boolean, reason?: string | null) => {
    const id = patient.id;
    const key = `prio:${id}`;
    if (!facilityId || !byId.get(id) || !canMark || offline || pendingIds.has(key)) return false;
    setMessage("");
    setPendingIds((s) => new Set(s).add(key));
    try {
      const r = await roundsApi.priority(facilityId, id, priority, reason);
      if (r && r.success && r.data) {
        const view: RoundPriority | null = r.data.priority;
        setRound((cur) => (cur ? {
          ...cur,
          residents: cur.residents.map((x) => (x.id === id ? { ...x, priority: view } : x)),
          priority_count: cur.residents.filter((x) => (x.id === id ? view : x.priority)).length,
        } : cur));
        return true;
      }
      setMessage(r && (r.message || r.error) ? `${r.message || r.error}` : `eMerge did not confirm that. ${patientName(patient)} is shown as it was; try again.`);
      return false;
    } catch {
      setMessage(`eMerge did not confirm that. ${patientName(patient)} is shown as it was; try again.`);
      return false;
    } finally {
      setPendingIds((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  }, [byId, canMark, facilityId, offline, pendingIds]);

  const setAddBack = useCallback(async (patient: RosterPatient, add: boolean, reason?: string | null) => {
    const id = patient.id;
    const key = `back:${id}`;
    if (!facilityId || !byId.get(id) || !canMark || offline || pendingIds.has(key)) return false;
    setMessage("");
    setPendingIds((s) => new Set(s).add(key));
    try {
      const r = await roundsApi.addBack(facilityId, id, add, reason);
      if (r && r.success && r.data) {
        const view: RoundAddedBack | null = r.data.added_back;
        setRound((cur) => (cur ? { ...cur, residents: cur.residents.map((x) => (x.id === id ? { ...x, added_back: view } : x)) } : cur));
        return true;
      }
      setMessage(r && (r.message || r.error) ? `${r.message || r.error}` : `eMerge did not confirm that. ${patientName(patient)} is shown as it was; try again.`);
      return false;
    } catch {
      setMessage(`eMerge did not confirm that. ${patientName(patient)} is shown as it was; try again.`);
      return false;
    } finally {
      setPendingIds((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  }, [byId, canMark, facilityId, offline, pendingIds]);

  const undoLast = useCallback(() => {
    if (!undo || !roster) return;
    const p = (wholeRows || roster.rows).find((r) => r.id === undo.id);
    if (p) mark(p, !undo.seen);
    setUndo(null);
  }, [undo, roster, mark, wholeRows]);

  const rowClass = useCallback((id: string) => {
    const e = on ? byId.get(id) : undefined;
    return [
      "fw-round-row",
      e ? (e.seen ? "is-seen" : "is-open") : "",
      lingering.has(id) ? "is-just-seen" : "",
      focusId === id ? "is-next" : "",
      e && e.priority ? "is-priority" : "",
    ].filter(Boolean).join(" ");
  }, [on, byId, lingering, focusId]);

  return {
    status,
    round: on ? round : null,
    filter,
    setFilter,
    entry: (id: string) => (on ? byId.get(id) : undefined),
    shows,
    mark,
    pending: (id: string) => pendingIds.has(id),
    canMark,
    offline,
    next,
    goNext,
    focusId,
    undo,
    undoLast,
    message,
    offered,
    offer: setOffered,
    rowClass,
    filters,
    setFilters,
    resetFilters: () => { setFilters(NO_FILTERS); setFilter("remaining"); },
    activeCount,
    matches,
    listRows,
    arrange,
    priorityFirst: on && priorityFirst,
    setPriorityFirst,
    priorityReady: Boolean(on && round && round.priority_ready),
    setPriority,
    addBackReady: Boolean(on && round && round.add_back_ready),
    setAddBack,
    facilityId,
    whole: { wanted: wantWhole, loaded: Boolean(wholeRows && wantWhole), loading: wholeLoading, capped: Boolean(roster && roster.total > WHOLE_LIMIT), total: roster ? roster.total : 0 },
  };
}

/**
 * The priority star on a row's chip line (and in the workbench). One tap flags the resident; the reason (optional, at
 * most 100 characters) and clearing are in the small editor the star opens. Shared with the building's providers, not
 * per day, a provider-side note only.
 */
export function PriorityChip({ patient, rounds, large = false }: { patient: RosterPatient; rounds: RoundsState; large?: boolean }) {
  const e = rounds.entry(patient.id);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  if (!e || !rounds.priorityReady) return null;
  const name = patientName(patient);
  const busy = rounds.pending(`prio:${patient.id}`);
  const p = e.priority || null;
  // "set by you" / "set by Rui" / "set by the office · Gwen" (a VA's prep from Manifest Review, ruling 2026-10-27).
  const office = Boolean(p && p.kind === "OFFICE");
  const who = p ? (p.by.mine ? "you" : office ? `the office · ${p.by.first_name}` : p.by.first_name) : "";
  const cls = `fw-prio-pill${p ? " is-on" : ""}${large ? " is-large" : ""}`;
  if (!rounds.canMark) {
    return p ? (
      <span className={`${cls}${office ? " is-office" : ""}`} aria-label={`${name} is a priority${p.reason ? `: ${p.reason}` : ""}, set by ${who}.`}>
        <Star aria-hidden="true" /> Priority{p.reason ? <small> · {p.reason}</small> : null}
        <small className="fw-prio-by"> · {who}</small>
      </span>
    ) : null;
  }
  const open = () => { setReason(p && p.reason ? p.reason : ""); setEditing(true); };
  return (
    <>
      <button
        type="button"
        className={`${cls}${office ? " is-office" : ""}`}
        aria-pressed={p ? "true" : "false"}
        aria-expanded={editing}
        aria-label={p
          ? `${name} is a priority${p.reason ? `: ${p.reason}` : ""}, set by ${who}${p.at ? ` at ${time(p.at)}` : ""}. Change or clear.`
          : `Flag ${name} as a priority`}
        disabled={busy || rounds.offline}
        onClick={async () => {
          if (p) { if (editing) setEditing(false); else open(); return; }
          // One tap flags; the reason is optional and can be added in the editor that opens.
          if (await rounds.setPriority(patient, true, null)) { setReason(""); setEditing(true); }
        }}
      >
        <Star aria-hidden="true" className={p ? "fw-prio-star" : undefined} /> Priority
        {p && p.reason ? <small> · {p.reason}</small> : null}
        {p ? <small className="fw-prio-by"> · {who}</small> : null}
      </button>
      {editing && (
        <form
          className="fw-prio-editor"
          aria-label={`Priority for ${name}`}
          onSubmit={async (ev) => {
            ev.preventDefault();
            if (await rounds.setPriority(patient, true, reason)) setEditing(false);
          }}
        >
          <label htmlFor={`fw-prio-${patient.id}`}>Why a priority (optional)</label>
          <input
            id={`fw-prio-${patient.id}`}
            value={reason}
            maxLength={PRIORITY_REASON_MAX}
            placeholder="e.g. family meeting, hearing aid broken"
            onChange={(ev) => setReason(ev.target.value)}
          />
          <small className="fw-muted">{reason.length}/{PRIORITY_REASON_MAX} · seen by the providers who cover this building and the office, never sent anywhere</small>
          <div className="fw-prio-actions">
            <Button type="submit" disabled={busy || rounds.offline}>Save reason</Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy || rounds.offline}
              onClick={async () => { if (await rounds.setPriority(patient, false)) setEditing(false); }}
            >
              Clear priority
            </Button>
            <Button type="button" variant="outline" onClick={() => setEditing(false)}>Done</Button>
          </div>
        </form>
      )}
    </>
  );
}

/**
 * "Add back" (April Torino via Isaiah Cotto, 2026-10-27): a resident off the Current manifest (screened DNQ, annual not
 * due) is put back on the NEXT manifest, with an optional reason (at most 100 characters). Shown on the viewer's own
 * rows: the marker when added back (who and why), the button when the row is off the manifest.
 */
export function AddBackChip({ patient, rounds, large = false }: { patient: RosterPatient; rounds: RoundsState; large?: boolean }) {
  const e = rounds.entry(patient.id);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  if (!e || !rounds.addBackReady) return null;
  const name = patientName(patient);
  const busy = rounds.pending(`back:${patient.id}`);
  const back = e.added_back || null;
  const off = patient.manifest_exclusion === "SCREENED_DNQ";
  if (!back && !off) return null;
  const who = back ? (back.by.mine ? "you" : back.kind === "OFFICE" ? `the office · ${back.by.first_name}` : back.by.first_name) : "";
  if (back) {
    const label = `${name} is added back to the next manifest${back.reason ? `: ${back.reason}` : ""}, by ${who}.`;
    return (
      <>
        {rounds.canMark ? (
          <button
            type="button"
            className={`fw-back-pill is-on${large ? " is-large" : ""}`}
            aria-pressed="true"
            aria-expanded={editing}
            aria-label={`${label} Change or take back.`}
            disabled={busy || rounds.offline}
            onClick={() => { setReason(back.reason || ""); setEditing((x) => !x); }}
          >
            <ListPlus aria-hidden="true" /> Added back{back.reason ? <small> · {back.reason}</small> : null}<small> · {who}</small>
          </button>
        ) : (
          <span className={`fw-back-pill is-on${large ? " is-large" : ""}`} aria-label={label}>
            <ListPlus aria-hidden="true" /> Added back{back.reason ? <small> · {back.reason}</small> : null}<small> · {who}</small>
          </span>
        )}
        {editing && rounds.canMark && (
          <form className="fw-prio-editor fw-back-editor" aria-label={`Added back: ${name}`}
            onSubmit={async (ev) => { ev.preventDefault(); if (await rounds.setAddBack(patient, true, reason)) setEditing(false); }}>
            <label htmlFor={`fw-back-${patient.id}`}>Why add them back (optional)</label>
            <input id={`fw-back-${patient.id}`} value={reason} maxLength={PRIORITY_REASON_MAX} onChange={(ev) => setReason(ev.target.value)} />
            <div className="fw-prio-actions">
              <Button type="submit" disabled={busy || rounds.offline}>Save reason</Button>
              <Button type="button" variant="outline" disabled={busy || rounds.offline}
                onClick={async () => { if (await rounds.setAddBack(patient, false)) setEditing(false); }}>Take off the next manifest</Button>
              <Button type="button" variant="outline" onClick={() => setEditing(false)}>Done</Button>
            </div>
          </form>
        )}
      </>
    );
  }
  if (!rounds.canMark) return null;
  return (
    <>
      <button
        type="button"
        className={`fw-back-pill${large ? " is-large" : ""}`}
        aria-pressed="false"
        aria-expanded={editing}
        aria-label={`Add ${name} back to the next manifest`}
        disabled={busy || rounds.offline}
        onClick={() => { setReason(""); setEditing((x) => !x); }}
      >
        <ListPlus aria-hidden="true" /> Add back
      </button>
      {editing && (
        <form className="fw-prio-editor fw-back-editor" aria-label={`Add back: ${name}`}
          onSubmit={async (ev) => { ev.preventDefault(); if (await rounds.setAddBack(patient, true, reason)) setEditing(false); }}>
          <label htmlFor={`fw-back-${patient.id}`}>Why add them back (optional)</label>
          <input id={`fw-back-${patient.id}`} value={reason} maxLength={PRIORITY_REASON_MAX}
            placeholder="e.g. family asked for a retest" onChange={(ev) => setReason(ev.target.value)} />
          <small className="fw-muted">
            On the Current manifest until a visit day after today ends with them seen or decided; a mark today does not end it. Testing status and tags are unchanged.
          </small>
          <div className="fw-prio-actions">
            <Button type="submit" disabled={busy || rounds.offline}>Add back to the next manifest</Button>
            <Button type="button" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </form>
      )}
    </>
  );
}

/** The marks after the seen pill on a row's chip line: the priority star, then add back. */
export function RoundExtras({ patient, rounds, large = false }: { patient: RosterPatient; rounds: RoundsState; large?: boolean }) {
  return (
    <>
      <PriorityChip patient={patient} rounds={rounds} large={large} />
      <AddBackChip patient={patient} rounds={rounds} large={large} />
    </>
  );
}

/** "Added back" on a row the round does not describe (a chart-locked row): the summary fact only, no who or why. */
export function AddedBackMark({ patient, rounds }: { patient: RosterPatient & { added_back?: boolean }; rounds: RoundsState }) {
  if (!patient.added_back || rounds.entry(patient.id)) return null;
  return <StatusBadge tone="indigo"><ListPlus aria-hidden="true" /> Added back</StatusBadge>;
}

/**
 * Earlier days' rounds (FR-7, ruling 2026-10-27): read-only. Pick a visit day with seen marks; the residents you can act
 * on who were seen that day, by whom and when, with the outcome recorded that day, and the counts. Nothing is markable.
 */
export function EarlierRounds({ facilityId }: { facilityId: string }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<RoundHistory | null>(null);
  const [day, setDay] = useState<string>("");
  const [detail, setDetail] = useState<RoundHistory | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    let live = true;
    roundsApi.history(facilityId).then((r) => {
      if (!live) return;
      if (r && r.success && r.data) setList(r.data); else setError((r && (r.message || r.error)) || "Earlier rounds could not be read just now.");
    }).catch(() => { if (live) setError("Earlier rounds could not be read just now."); });
    return () => { live = false; };
  }, [open, facilityId]);
  useEffect(() => {
    if (!day) { setDetail(null); return; }
    let live = true;
    roundsApi.history(facilityId, day).then((r) => {
      if (!live) return;
      if (r && r.success && r.data) setDetail(r.data); else setError((r && (r.message || r.error)) || "That round could not be read just now.");
    }).catch(() => { if (live) setError("That round could not be read just now."); });
    return () => { live = false; };
  }, [day, facilityId]);
  const past = (list ? list.days : []).filter((d) => !d.today);
  return (
    <div className="fw-earlier">
      <Button type="button" variant="outline" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <History aria-hidden="true" /> Earlier rounds
      </Button>
      {open && (
        <div className="fw-earlier-panel" role="region" aria-label="Earlier rounds, read only">
          {error && <p role="alert" className="fw-error">{error}</p>}
          {list && !past.length && <p className="fw-muted">No earlier round at this building has seen marks in the last 90 days.</p>}
          {past.length > 0 && (
            <div className="fw-field">
              <span>Visit day</span>
              <FieldSelect
                label="Earlier visit day"
                value={day}
                onChange={setDay}
                options={[{ value: "", label: "Choose a day" }, ...past.map((d) => ({ value: d.visit_date, label: `${d.visit_date} · ${d.seen} seen` }))]}
              />
            </div>
          )}
          {detail && detail.visit_date && (
            <>
              <p className="fw-round-showing" role="status">
                {detail.visit_date}: <b>{detail.counts ? detail.counts.seen : 0}</b> of your residents seen
                {detail.counts && detail.counts.by_outcome.length ? ` · ${detail.counts.by_outcome.map((o) => `${o.label} ${o.count}`).join(" · ")}` : ""}
                {detail.not_yours ? ` · ${detail.not_yours} others seen, not yours to read` : ""} · read only
              </p>
              <ul className="fw-earlier-list">
                {detail.residents.map((r) => (
                  <li key={r.id}>
                    <strong>{[r.first_name, r.last_name].filter(Boolean).join(" ") || "Resident"}</strong>
                    <span className="fw-muted"> · Room {r.room || "not recorded"} · seen by {r.by.mine ? "you" : r.by.first_name} {time(r.seen_at)}{r.source === "TWL_Q" ? " · auto" : ""}</span>
                    {r.outcome && <StatusBadge tone={OUTCOME_TONE[r.outcome.key] || "neutral"}>{r.outcome.label}</StatusBadge>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** The seen control on a row's chip line (or in the workbench): one tap marks seen; tapping a seen mark undoes it. */
export function SeenChip({ patient, rounds, large = false }: { patient: RosterPatient; rounds: RoundsState; large?: boolean }) {
  const e = rounds.entry(patient.id);
  if (!e) return null; // not the viewer's to act on (chart locked), or rounds off
  const name = patientName(patient);
  const busy = rounds.pending(patient.id);
  const outcome = e.outcome ? (
    <StatusBadge tone={OUTCOME_TONE[e.outcome.key] || "neutral"}>
      <span className="fw-sr-only">Today&rsquo;s outcome: </span>
      {e.outcome.label}
    </StatusBadge>
  ) : null;
  // "Seen by <first name> 10:42" for a colleague's mark; "you" for the viewer's own.
  const who = e.by ? (e.by.mine ? "you" : e.by.first_name) : "";
  const auto = e.source === "TWL_Q";
  if (e.seen) {
    const words = `Seen${auto ? " · auto" : ""}`;
    const detail = `${who}${e.at ? ` ${time(e.at)}` : ""}`;
    const label = `${name}: seen by ${who || "a provider"}${e.at ? ` at ${time(e.at)}` : ""}${auto ? ", marked when the TWL Q outcome was recorded" : ""}.`;
    if (!rounds.canMark) {
      return (
        <>
          <span className={`fw-seen-pill is-seen${auto ? " is-auto" : ""}${large ? " is-large" : ""}`} aria-label={label}>
            <Check aria-hidden="true" /> {words}
            {detail && <small> · {detail}</small>}
          </span>
          {outcome}
          <RoundExtras patient={patient} rounds={rounds} large={large} />
        </>
      );
    }
    return (
      <>
        <button
          type="button"
          data-seen-control
          className={`fw-seen-pill is-seen${auto ? " is-auto" : ""}${large ? " is-large" : ""}`}
          aria-pressed="true"
          aria-label={`${label} Tap to undo.`}
          disabled={busy || rounds.offline}
          onClick={() => rounds.mark(patient, false)}
        >
          <Check aria-hidden="true" className="fw-seen-check" /> {words}
          {detail && <small> · {detail}</small>}
        </button>
        {outcome}
        <RoundExtras patient={patient} rounds={rounds} large={large} />
      </>
    );
  }
  if (!rounds.canMark) return <>{outcome}<RoundExtras patient={patient} rounds={rounds} large={large} /></>;
  return (
    <>
      <button
        type="button"
        data-seen-control
        className={`fw-seen-pill is-open${large ? " is-large" : ""}${rounds.offered === patient.id ? " is-offered" : ""}`}
        aria-pressed="false"
        aria-label={`Mark ${name} seen`}
        disabled={busy || rounds.offline}
        onClick={() => rounds.mark(patient, true)}
      >
        <Circle aria-hidden="true" /> Mark seen
      </button>
      {outcome}
      <RoundExtras patient={patient} rounds={rounds} large={large} />
    </>
  );
}

/** The seen marks in the legend, drawn exactly as they look on a row (the legend is demonstrative). */
export function SeenLegend({ rounds }: { rounds: RoundsState }) {
  if (!rounds.round) return null;
  return (
    <span className="fw-seen-legend" aria-label="Rounds legend">
      <span className="fw-seen-pill is-open is-sample" aria-hidden="true"><Circle /> Mark seen</span>
      <span className="fw-seen-pill is-seen is-sample" aria-hidden="true"><Check /> Seen</span>
      <span className="fw-seen-pill is-seen is-auto is-sample" aria-hidden="true"><Check /> Seen · auto</span>
      {rounds.priorityReady && <span className="fw-prio-pill is-on is-sample" aria-hidden="true"><Star className="fw-prio-star" /> Priority</span>}
      <span className="fw-sr-only">
        Mark seen: not yet seen today. Seen: a provider marked the resident seen. Seen · auto: marked when a TWL Q outcome
        was recorded.{rounds.priorityReady ? " Priority: flagged by a provider to see first." : ""}
      </span>
    </span>
  );
}

function ProgressBar({ round }: { round: Round }) {
  const { total, seen } = round.progress;
  const twlq = round.residents.filter((r) => r.seen && r.outcome && r.outcome.key === "TWL_Q").length;
  const other = round.residents.filter((r) => r.seen && r.outcome && r.outcome.key !== "TWL_Q").length;
  const plain = seen - twlq - other;
  const pct = (n: number) => (total ? `${(n / total) * 100}%` : "0%");
  const segments = [
    { key: "twlq", n: twlq, label: "TWL Q" },
    { key: "other", n: other, label: "Other outcome today" },
    { key: "seen", n: plain, label: "Seen, no outcome yet" },
  ];
  return (
    <div className="fw-round-bar-wrap">
      <div
        className="fw-round-bar"
        role="progressbar"
        aria-label="Round progress"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={seen}
        aria-valuetext={`${seen} of ${total} seen`}
      >
        {segments.map((s) => (
          <span key={s.key} className={`fw-round-seg is-${s.key}`} style={{ width: pct(s.n) }} data-count={s.n} />
        ))}
      </div>
      <ul className="fw-round-key" aria-label="Progress legend">
        {segments.filter((s) => s.n > 0).map((s) => (
          <li key={s.key}><i className={`fw-round-swatch is-${s.key}`} aria-hidden="true" />{s.label} <strong>{s.n}</strong></li>
        ))}
        <li><i className="fw-round-swatch is-open" aria-hidden="true" />Remaining <strong>{round.progress.remaining}</strong></li>
      </ul>
    </div>
  );
}

/**
 * Renata's filter (2026-10-27): testing chip, today's outcome, priority, floor; combined with Remaining / Seen / All.
 * AND across groups, OR within one. A disclosure keeps the phone screen short; the count of filters in force is on it.
 */
export function RoundsFilterBar({ rounds, sheet }: { rounds: RoundsState; sheet: DaySheet }) {
  const [open, setOpen] = useState(false);
  const f = rounds.filters;
  const floors = useMemo(() => {
    const keys = new Set(rounds.listRows(sheet.roster.rows).map((p) => floorOf(p.room_number)));
    const order = (k: string) => (k === "low" ? -1 : k === "other" ? 1e6 : k === "none" ? 1e6 + 1 : parseInt(k, 10));
    return Array.from(keys).sort((a, b) => order(a) - order(b));
  }, [rounds, sheet]);
  const count = rounds.activeCount;
  return (
    <div className="fw-round-filters">
      <div className="fw-round-filters-bar">
        <Button
          type="button"
          variant="outline"
          className="fw-round-filters-toggle"
          aria-expanded={open}
          aria-controls="fw-round-filters-panel"
          onClick={() => setOpen((o) => !o)}
        >
          <SlidersHorizontal aria-hidden="true" /> Filters{count ? <span className="fw-round-count-badge" aria-label={`${count} on`}>{count}</span> : null}
        </Button>
        {count > 0 && (
          <Button type="button" variant="outline" onClick={rounds.resetFilters}>Reset filters</Button>
        )}
      </div>
      {open && (
        <div className="fw-round-filters-panel" id="fw-round-filters-panel" role="group" aria-label="Filter residents">
          <div className="fw-round-facet">
            <span className="fw-round-facet-label" id="fw-facet-testing">Testing</span>
            <ToggleGroup
              type="multiple"
              value={f.testing}
              onValueChange={(v: string[]) => rounds.setFilters({ ...f, testing: v })}
              aria-labelledby="fw-facet-testing"
              className="fw-round-chips"
            >
              {TESTING_FILTERS.map((t) => (
                <ToggleGroupItem key={t.key} value={t.key} className={`fw-filter-chip tone-${t.tone}`} aria-label={`Testing: ${t.label}`}>
                  {f.testing.includes(t.key) ? <Check aria-hidden="true" /> : null}{t.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
          <div className="fw-round-facet">
            <span className="fw-round-facet-label" id="fw-facet-outcome">Today&rsquo;s outcome</span>
            <ToggleGroup
              type="multiple"
              value={f.outcomes}
              onValueChange={(v: string[]) => rounds.setFilters({ ...f, outcomes: v })}
              aria-labelledby="fw-facet-outcome"
              className="fw-round-chips"
            >
              {OUTCOME_FILTERS.map((o) => (
                <ToggleGroupItem key={o.key} value={o.key} className={`fw-filter-chip tone-${o.key === "NONE" ? "neutral" : OUTCOME_TONE[o.key] || "neutral"}`} aria-label={`Outcome: ${o.label}`}>
                  {f.outcomes.includes(o.key) ? <Check aria-hidden="true" /> : null}{o.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
          <div className="fw-round-facet fw-round-facet-row">
            {rounds.priorityReady && (
              <button
                type="button"
                className={`fw-prio-pill${f.priorityOnly ? " is-on" : ""}`}
                aria-pressed={f.priorityOnly}
                onClick={() => rounds.setFilters({ ...f, priorityOnly: !f.priorityOnly })}
              >
                <Star aria-hidden="true" className={f.priorityOnly ? "fw-prio-star" : undefined} /> Priority only
              </button>
            )}
            {floors.length > 1 && (
              <div className="fw-field fw-round-floor">
                <span>Floor</span>
                <FieldSelect
                  label="Floor"
                  value={f.floor}
                  onChange={(v) => rounds.setFilters({ ...f, floor: v })}
                  options={[{ value: "", label: "Every floor" }, ...floors.map((k) => ({ value: k, label: floorLabel(k) }))]}
                />
              </div>
            )}
          </div>
          <p className="fw-muted">
            Filters combine: a resident is listed only when every group you chose matches. Outcome and priority are known
            for the residents you can act on.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * The round's header, beside the Worklist and Walk order controls: "Seen n of N", the bar, what is left, the last
 * mark, the Remaining / Seen / All control and "Next resident"; the summary when everyone is seen.
 */
export function RoundsHeader({ rounds, sheet, shown, onOpenResident }: { rounds: RoundsState; sheet: DaySheet; shown?: number; onOpenResident?: (id: string) => void }) {
  const r = rounds.round;
  if (rounds.status === "loading" && !r) {
    return <p className="fw-muted fw-round-wait" role="status">Reading today&rsquo;s round…</p>;
  }
  if (rounds.status === "error" && !r) {
    return <p className="fw-muted" role="status">Today&rsquo;s round could not be read just now. The manifest is unchanged.</p>;
  }
  if (!r) return null;
  const { total, seen, remaining, complete, last_seen_at: last } = r.progress;
  if (total === 0) {
    return (
      <p className="fw-muted fw-round-none" role="status">
        Today&rsquo;s round: none of the {r.worklist_total} residents on this worklist are yours to mark. You do not cover
        this building, and no care assignment names them.
      </p>
    );
  }
  const n = rounds.next();
  const paged = sheet.roster.total > sheet.roster.returned;
  const narrowed = rounds.filter !== "all" || rounds.activeCount > 0 || rounds.priorityFirst;
  return (
    <section className={`fw-round${complete ? " is-complete" : ""}`} aria-label="Today's round">
      <div className="fw-round-top">
        <div className="fw-round-count" aria-live="polite">
          <span className="fw-eyebrow">Today&rsquo;s round</span>
          <strong>
            {complete ? <CheckCheck aria-hidden="true" /> : null}
            Seen {seen} of {total}
          </strong>
          <small>
            {complete ? "Round complete: everyone is accounted for." : `${remaining} remaining`}
            {last ? ` · last seen ${time(last)}` : ""}
          </small>
        </div>
        {r.can_mark && !complete && (
          <Button
            type="button"
            className="fw-round-next"
            onClick={rounds.goNext}
            disabled={!n}
            aria-label={n ? "Next resident: go to the next one not yet seen, in walk order" : "Everyone is seen"}
          >
            Next resident <ArrowRight aria-hidden="true" />
          </Button>
        )}
      </div>
      <ProgressBar round={r} />
      {/* Facility records Phase 0c (2026-10-27): the check-out that sends the day's visit notes and audiograms. */}
      {r.can_mark && seen > 0 && (
        <div className="fw-round-closeout">
          <CloseOutVisit facilityId={sheet.facility.id} seenCount={seen} onOpenResident={onOpenResident} />
        </div>
      )}
      {complete && (
        <div className="fw-round-summary" role="status">
          <span>Today&rsquo;s outcomes:</span>
          {r.tally.by_outcome.map((t) => (
            <StatusBadge key={t.key} tone={OUTCOME_TONE[t.key] || "neutral"}>{t.label} {t.count}</StatusBadge>
          ))}
          {r.tally.no_outcome > 0 && <StatusBadge>No outcome recorded {r.tally.no_outcome}</StatusBadge>}
        </div>
      )}
      <div className="fw-round-filter">
        <ToggleGroup
          type="single"
          variant="outline"
          value={rounds.filter}
          onValueChange={(v) => { if (v) rounds.setFilter(v as RoundsFilter); }}
          aria-label="Show residents"
          className="fw-round-toggle"
        >
          <ToggleGroupItem value="remaining" aria-label={`Remaining, ${remaining}`}>Remaining <b>{remaining}</b></ToggleGroupItem>
          <ToggleGroupItem value="seen" aria-label={`Seen, ${seen}`}>Seen <b>{seen}</b></ToggleGroupItem>
          <ToggleGroupItem value="all" aria-label="All residents">All</ToggleGroupItem>
        </ToggleGroup>
        <RoundsFilterBar rounds={rounds} sheet={sheet} />
      </div>
      <EarlierRounds facilityId={sheet.facility.id} />
      {typeof shown === "number" && (
        <p className="fw-round-showing" role="status">
          {paged && !rounds.whole.loaded ? (
            <>
              Showing <b>{shown}</b> of the <b>{sheet.roster.returned}</b> residents on this page
              {` (${sheet.roster.offset + 1}–${sheet.roster.offset + sheet.roster.returned} of ${sheet.roster.total})`}
            </>
          ) : (
            <>Showing <b>{shown}</b> of <b>{sheet.roster.total}</b> residents on this worklist</>
          )}
          {rounds.activeCount ? ` · ${rounds.activeCount} filter${rounds.activeCount === 1 ? "" : "s"} on` : ""}
          {rounds.priorityFirst ? " · priority first" : ""}
          {rounds.whole.loading ? " · reading the whole worklist…" : ""}
          {rounds.whole.wanted && !rounds.whole.loaded && !rounds.whole.loading ? " · this page only (the whole worklist could not be read)" : ""}
          {rounds.whole.loaded && rounds.whole.capped ? ` · the first ${WHOLE_LIMIT} only` : ""}
        </p>
      )}
      <p className="fw-muted fw-round-scope">
        The count above is the whole worklist you can act on{r.not_yours ? `; ${r.not_yours} others here are not yours to mark` : ""}.
        {r.capped ? ` It covers the first ${WHOLE_LIMIT} residents of this worklist only.` : ""}
        {narrowed ? " The filters narrow the list, not the count." : ""}
        {!r.can_mark && r.closed ? ` ${r.closed}: seen and priority marks cannot be made here.` : ""}
        {!r.can_mark && !r.closed ? " This account cannot mark residents here." : ""}
      </p>
      {rounds.message && <p role="alert" className="fw-error">{rounds.message}</p>}
      {rounds.undo && (
        <div className="fw-round-undo" role="status">
          <span>{rounds.undo.seen ? `Marked ${rounds.undo.name} seen.` : `${rounds.undo.name} is not seen.`}</span>
          <Button type="button" variant="outline" onClick={rounds.undoLast} disabled={rounds.offline}>
            <Undo2 aria-hidden="true" /> Undo
          </Button>
        </div>
      )}
    </section>
  );
}

/** The seen control at the top of the resident workbench (a portal: it reads the round through RoundsContext). */
export function WorkbenchSeen({ patient }: { patient: RosterPatient }) {
  const rounds = useContext(RoundsContext);
  if (!rounds || !rounds.entry(patient.id)) return null;
  return (
    <div className={`fw-workbench-seen${rounds.offered === patient.id ? " is-offered" : ""}`} aria-label="Today's round">
      {rounds.offered === patient.id && !rounds.entry(patient.id)?.seen && (
        <span className="fw-muted">Saved. Did you see them on this round?</span>
      )}
      <SeenChip patient={patient} rounds={rounds} large />
    </div>
  );
}

/** For the workbench's save: offer "seen" after notes or tags were saved for this resident (never implied). */
export function useOfferSeen(): (id: string) => void {
  const rounds = useContext(RoundsContext);
  return useCallback((id: string) => { if (rounds) rounds.offer(id); }, [rounds]);
}
